import { UserProfileRole } from '@@types/api.types';
import { AttachmentCategory, type AttachmentSelectDto, AttachmentType } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Tile } from '@components/shared/tile.component';
import type { AttachmentTargetType } from '@queries/attachments.query';
import { useGetCurrentUser } from '@queries/users.query';
import {
  downloadFile,
  isImageContentType,
  resolveAttachmentOpenUrl,
  resolveDownloadFileName,
} from '@utils/attachment-download.util';
import { DownloadIcon, EditIcon, ExternalLinkIcon } from 'lucide-react';
import { useState } from 'react';
import type { FunctionComponent, ReactNode } from 'react';
import { AttachmentsList } from './attachments-list.component';
import { AttachmentsModal } from './attachments-modal.component';

type AttachmentsTileProps = {
  allowTechnologyImages?: boolean;
  attachments?: AttachmentSelectDto[];
  canEdit?: boolean;
  category: AttachmentCategory;
  className?: string;
  defaultType?: AttachmentType;
  emptyMessage?: string;
  emptyTitle?: string;
  isAttachmentsLoading?: boolean;
  targetId?: string;
  targetType?: AttachmentTargetType;
  title?: string;
};

export const AttachmentsTile: FunctionComponent<AttachmentsTileProps> = (props) => {
  const {
    allowTechnologyImages = false,
    attachments = [],
    canEdit: canEditProp,
    category,
    className,
    defaultType,
    emptyMessage,
    emptyTitle = 'No Attachments',
    isAttachmentsLoading = false,
    targetId,
    targetType = 'technology',
    title = 'Attachments',
  } = props;

  const [attachmentToEdit, setAttachmentToEdit] = useState<AttachmentSelectDto | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: user } = useGetCurrentUser();
  const canEdit = canEditProp ?? (user?.profile?.role === UserProfileRole.INNOVATOR && Boolean(targetId));

  const isEmpty = attachments.length === 0;
  const isTechnologyInlineMode = allowTechnologyImages && targetType === 'technology';
  const currentFeaturedTechnologyImageId = attachments.find((attachment) => {
    return attachment.category === AttachmentCategory.TECHNOLOGY_IMAGE && attachment.featured;
  })?.id;

  const isImageAttachment = (attachment: AttachmentSelectDto): boolean => {
    return attachment.type === AttachmentType.FILE && isImageContentType(attachment.contentType);
  };

  const handleOpenModal = (attachment?: AttachmentSelectDto): void => {
    setAttachmentToEdit(attachment ?? null);
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setAttachmentToEdit(null);
    setIsModalOpen(false);
  };

  const handleOpenAttachment = (attachment: AttachmentSelectDto): void => {
    const openUrl = resolveAttachmentOpenUrl(attachment);

    if (!openUrl) {
      return;
    }

    window.open(openUrl, '_blank', 'noopener,noreferrer');
  };

  const renderTechnologyInlineActions = (attachment: AttachmentSelectDto): ReactNode => {
    const downloadUrl = attachment.type === AttachmentType.FILE ? attachment.downloadUrl : undefined;
    const isImage = isImageAttachment(attachment);
    const openUrl = resolveAttachmentOpenUrl(attachment);

    return (
      <div className="flex items-center gap-1">
        <IconButton
          ariaLabel="Edit attachment"
          icon={<EditIcon size={16} />}
          onClick={() => handleOpenModal(attachment)}
          size="sm"
          variant="secondary"
        />
        {attachment.type === AttachmentType.FILE && !isImage ? (
          <IconButton
            ariaLabel="Download attachment"
            disabled={!downloadUrl}
            icon={<DownloadIcon size={16} />}
            onClick={() => {
              if (!downloadUrl) {
                return;
              }

              downloadFile({
                fileName: resolveDownloadFileName(attachment),
                url: downloadUrl,
              });
            }}
            size="sm"
            variant="secondary"
          />
        ) : (
          <IconButton
            ariaLabel="Open attachment"
            disabled={!openUrl}
            icon={<ExternalLinkIcon size={16} />}
            onClick={() => handleOpenAttachment(attachment)}
            size="sm"
            variant="secondary"
          />
        )}
      </div>
    );
  };

  return (
    <>
      <Tile
        className={className}
        emptyState={{
          message: canEdit ? 'Add Attachment' : emptyMessage,
          onClick: canEdit ? () => handleOpenModal() : undefined,
          title: emptyTitle,
        }}
        header={{
          button: canEdit && !isEmpty ? { onClick: () => handleOpenModal(), text: 'Add +' } : undefined,
          title,
        }}
        isEmpty={isEmpty}
        isLoading={isAttachmentsLoading}
      >
        <div className="max-h-[44rem] overflow-y-auto pr-1">
          <AttachmentsList
            attachments={attachments}
            disableFileDownload={false}
            disableLinkNavigation={false}
            onEdit={canEdit && !isTechnologyInlineMode ? handleOpenModal : undefined}
            onRowClick={canEdit && isTechnologyInlineMode ? handleOpenModal : undefined}
            renderActions={canEdit && isTechnologyInlineMode ? renderTechnologyInlineActions : undefined}
            showHeader={false}
            useFormLabel={false}
          />
        </div>
      </Tile>

      {targetId && canEdit && (
        <AttachmentsModal
          allowTechnologyImages={isTechnologyInlineMode}
          attachmentToEdit={attachmentToEdit}
          category={category}
          currentFeaturedTechnologyImageId={currentFeaturedTechnologyImageId}
          defaultType={defaultType}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          targetId={targetId}
          targetType={targetType}
        />
      )}
    </>
  );
};
